/**
 * Gerador de payload PIX estático (padrão EMV QR Code - Banco Central do Brasil)
 * Especificação: Manual de Padrões para Iniciação do PIX - BACEN
 * Funciona com qualquer app bancário (Nubank, Itaú, Bradesco, Caixa, BB, etc.)
 */

function crc16(str: string): string {
  let crc = 0xffff
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8
    for (let j = 0; j < 8; j++) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1
    }
  }
  return ((crc & 0xffff).toString(16).toUpperCase()).padStart(4, '0')
}

function emv(id: string, value: string): string {
  const len = value.length.toString().padStart(2, '0')
  return `${id}${len}${value}`
}

/**
 * Detecta o tipo de chave PIX e retorna o identificador correto
 */
function detectPixKeyType(key: string): string {
  const cleaned = key.replace(/\D/g, '')

  // CPF: 11 dígitos
  if (/^\d{11}$/.test(cleaned) && key.replace(/[.\-]/g, '').length === 11) {
    return 'CPF'
  }
  // CNPJ: 14 dígitos
  if (/^\d{14}$/.test(cleaned)) {
    return 'CNPJ'
  }
  // Telefone: começa com + ou tem 10-11 dígitos
  if (/^\+?\d{10,13}$/.test(cleaned)) {
    return 'PHONE'
  }
  // E-mail
  if (key.includes('@')) {
    return 'EMAIL'
  }
  // Chave aleatória (EVP): UUID ou string com 32+ chars
  return 'EVP'
}

export interface PixPayloadOptions {
  pixKey: string          // Chave PIX do beneficiário
  beneficiaryName: string // Nome do beneficiário (máx. 25 chars)
  city: string            // Cidade do beneficiário (máx. 15 chars)
  amount?: number         // Valor (opcional — se omitido, doador digita)
  txid?: string           // ID da transação (máx. 25 chars alfanumérico, sem espaço)
  description?: string    // Descrição/mensagem (máx. 72 chars)
}

/**
 * Gera o payload PIX EMV QR Code estático pronto para ser convertido em QR.
 * O resultado pode ser lido por qualquer app bancário brasileiro.
 */
export function buildPixPayload(opts: PixPayloadOptions): string {
  const {
    pixKey,
    beneficiaryName,
    city,
    amount,
    txid = '***',
    description = '',
  } = opts

  // Normaliza nome e cidade
  const name = beneficiaryName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .slice(0, 25)
    .toUpperCase()

  const cityNorm = city
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .slice(0, 15)
    .toUpperCase()

  const txidNorm = txid
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 25) || '***'

  // Merchant Account Information (MAI) — ID 26
  const keyType = detectPixKeyType(pixKey)
  const guiValue = 'BR.GOV.BCB.PIX'

  const maiInner = [
    emv('00', guiValue),                                       // GUI
    emv('01', pixKey),                                         // Chave PIX
    description ? emv('02', description.slice(0, 72)) : '',    // Descrição opcional
  ].join('')

  const mai = emv('26', maiInner)

  // Merchant Category Code (MCC) — 0000 = não categorizado
  const mcc = emv('52', '0000')

  // Transaction Currency — 986 = BRL
  const currency = emv('53', '986')

  // Transaction Amount (opcional)
  const amountStr = amount && amount > 0
    ? emv('54', amount.toFixed(2))
    : ''

  // Country Code
  const country = emv('58', 'BR')

  // Merchant Name
  const merchantName = emv('59', name)

  // Merchant City
  const merchantCity = emv('60', cityNorm)

  // Additional Data Field Template — ID 62 (txid)
  const adf = emv('62', emv('05', txidNorm))

  // Payload Format Indicator (obrigatório ser o primeiro campo)
  const pfi = emv('00', '01')

  // Point of Initiation Method — 12 = QR reutilizável
  const poim = emv('01', '12')

  // Monta payload sem CRC
  const payloadWithoutCrc = [pfi, poim, mai, mcc, currency, amountStr, country, merchantName, merchantCity, adf].join('')

  // CRC16 — ID 63, sempre 4 chars
  const crcField = '6304'
  const crc = crc16(payloadWithoutCrc + crcField)

  return payloadWithoutCrc + crcField + crc
}
